import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Linkedin, ExternalLink } from "lucide-react";
import { COMPANY } from "@/data/content";

const Footer = () => {
  const { t } = useTranslation("common");

  const NAV = [
    [t("nav.accueil"), "/", "accueil"],
    [t("nav.services"), "/services", "services"],
    [t("nav.investissements"), "/investissements", "investissements"],
    [t("nav.portfolio"), "/investissements/portfolio", "portfolio"],
    [t("nav.conformite"), "/conformite", "conformite"],
    [t("nav.cabinet"), "/le-cabinet", "le-cabinet"],
    [t("nav.equipe"), "/equipe", "equipe"],
    [t("nav.contact"), "/contact", "contact"],
  ];

  return (
    <footer data-testid="site-footer" className="border-t border-white/[0.08] bg-[#08090C]">
      <div className="mx-auto max-w-7xl px-6 py-12 sm:px-8 lg:px-12 lg:py-14">
        <div className="flex flex-col gap-10 lg:flex-row lg:items-start lg:gap-20">
          <div className="shrink-0">
            <img
              src="/logo-full.jpg"
              alt="Gabana Management — Genève"
              className="w-52 object-contain mix-blend-screen"
            />
            <p className="mt-5 max-w-xs text-sm leading-relaxed text-slate2">{t("footer.desc")}</p>
            <div className="mt-5 flex flex-wrap gap-3">
              <a
                href={COMPANY.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-linkedin-link"
                aria-label="Gabana Management SA sur LinkedIn"
                className="inline-flex items-center gap-2 border border-white/10 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.18em] text-mist/70 transition-colors duration-200 hover:border-brass/50 hover:text-chalk"
              >
                <Linkedin size={13} />
                LinkedIn
              </a>
              <a
                href={COMPANY.crunchbase}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-crunchbase-link"
                aria-label="Gabana Management SA sur Crunchbase"
                className="inline-flex items-center gap-2 border border-white/10 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.18em] text-mist/70 transition-colors duration-200 hover:border-brass/50 hover:text-chalk"
              >
                <ExternalLink size={13} />
                Crunchbase
              </a>
            </div>
          </div>

          <div className="flex flex-col gap-10 sm:flex-row sm:gap-16">
            <nav aria-label="Navigation de pied de page">
              <p className="overline-label mb-4">{t("footer.pagesTitle")}</p>
              <ul className="space-y-2.5 text-sm">
                {NAV.map(([label, to, id]) => (
                  <li key={to}>
                    <Link
                      to={to}
                      data-testid={`footer-link-${id}`}
                      className="text-mist/70 transition-colors duration-200 hover:text-chalk"
                    >
                      {label}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>

            <div>
              <p className="overline-label mb-4">{t("footer.infoTitle")}</p>              <address className="space-y-1.5 text-xs not-italic leading-relaxed text-mist/60">
                <p>
                  <span className="text-slate2/70">{t("footer.legalName")} : </span>
                  <span className="text-chalk">{COMPANY.name}</span>
                </p>
                <p>
                  <span className="text-slate2/70">{t("footer.address")} : </span>
                  {COMPANY.address}
                </p>
                <p>
                  <span className="text-slate2/70">{t("footer.website")} : </span>
                  <span data-testid="legal-website" className="text-chalk">{COMPANY.website}</span>
                </p>
                <p>
                  <span className="text-slate2/70">{t("footer.email")} : </span>
                  <a href={`mailto:${COMPANY.email}`} data-testid="legal-email" className="text-chalk transition-colors duration-200 hover:text-brass">
                    {COMPANY.email}
                  </a>
                </p>
                <p>
                  <span className="text-slate2/70">{t("footer.rc")} : </span>
                  <span data-testid="legal-reg-number">{COMPANY.regNumber}</span>
                </p>                <p>
                  <span className="text-slate2/70">{t("footer.tva")} : </span>
                  <span data-testid="legal-tva-number">{COMPANY.tva}</span>
                </p>
                <p>
                  <span className="text-slate2/70">{t("footer.registered")} : </span>
                  {COMPANY.registered}
                </p>
              </address>
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-white/[0.06]">
        <div className="mx-auto flex max-w-7xl flex-col gap-2 px-6 py-5 font-mono text-[10px] uppercase tracking-[0.2em] text-slate2/70 sm:px-8 lg:flex-row lg:items-center lg:justify-between lg:px-12">
          <p>© 1999 – 2026 Gabana Management SA — {t("footer.rights")}</p>
          <div className="flex items-center gap-6">
            <p>{t("footer.made")}</p>
            <Link
              to="/mentions-legales"
              data-testid="footer-link-mentions-legales"
              className="text-slate2/70 transition-colors duration-200 hover:text-brass"
            >
              {t("nav.mentions")}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
